import React, { memo, useCallback, useMemo } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import type { State } from '@racepointenergy/swc/scs/features/host-states'

import { Table } from 'components/Table'
import type { TableHeader } from 'components/Table/types'
import HomeSystemStateValue from './HomeSystemStateValue'
import { sortByInsensitiveString } from 'components/Table/utils'
import { HomeSystemStatesBlock } from './HomeSystemStatesBlock'
import {
  HomeSystemStatesCellName,
  HomeSystemStatesCellStyles,
  HomeSystemStatesRowStyles,
  HomeSystemStatesTableInnerWrapper,
  HomeSystemStatesTableOuterWrapper,
} from './styles'

interface HomeSystemStatesTableProps {
  headers: Record<string, TableHeader>
  states: State[]
  defaultSortBy: SortingRule<State>
  sortBy: SortingRule<State>
  setSortBy: (sortBy: SortingRule<State>) => void
  searchValue?: string
}

function HomeSystemStatesTable({
  headers,
  states,
  defaultSortBy,
  sortBy,
  setSortBy,
  searchValue,
}: HomeSystemStatesTableProps) {
  const StateCell = useCallback(
    (cell: Cell) => (
      <HomeSystemStatesCellName>{cell.value}</HomeSystemStatesCellName>
    ),
    []
  )
  const StateValueCell = useCallback(
    ({ value, row: { original, index } }: Cell<State>) => (
      <HomeSystemStateValue key={index} value={value} type={original.type} />
    ),
    []
  )

  const columns: readonly Column<State>[] = useMemo(
    () => [
      {
        Header: headers.state.label,
        id: headers.state.id,
        accessor: (row) => row.state,
        Cell: StateCell,
        cellStyles: HomeSystemStatesCellStyles,
        percentWidth: '30%',
        sortType: sortByInsensitiveString,
      },
      {
        Header: headers.type.label,
        id: headers.type.id,
        accessor: (row) => row.type,
        cellStyles: HomeSystemStatesCellStyles,
        disableGlobalFilter: true,
        percentWidth: '10%',
      },
      {
        Header: headers.value.label,
        id: headers.value.id,
        accessor: (row) => row.value,
        Cell: StateValueCell,
        cellStyles: HomeSystemStatesCellStyles,
        disableGlobalFilter: true,
        percentWidth: '50%',
        sortType: sortByInsensitiveString,
      },
    ],
    [StateCell, StateValueCell, headers]
  )

  return (
    <HomeSystemStatesTableOuterWrapper>
      <HomeSystemStatesTableInnerWrapper>
        <Table
          columns={columns}
          data={states}
          defaultSortBy={defaultSortBy}
          sortBy={sortBy}
          setSortBy={setSortBy}
          searchValue={searchValue}
          stripedRows={true}
          rowStyles={HomeSystemStatesRowStyles}
          mobileViewComponent={HomeSystemStatesBlock}
        />
      </HomeSystemStatesTableInnerWrapper>
    </HomeSystemStatesTableOuterWrapper>
  )
}

export default memo(HomeSystemStatesTable)
